import { useId, useState } from 'react';
import { ChevronRightIcon, CircleDashedIcon } from 'lucide-react';

import type { PeriodBounds } from '@/lib/api/generated/model';
import { profileFacts } from '@/features/llm-profiles/profileSummary';
import { formatTokens } from '@/features/workspace-usage/money';
import { cn } from '@/lib/utils';
import { DeletedLabel } from '@/shared/components/DeletedLabel';
import { EyebrowLabel } from '@/shared/components/EyebrowLabel';
import {
  FIGURE_BAR,
  FIGURE_BAR_TRACK,
  FIGURE_BODY,
  FIGURE_CELL,
  FIGURE_FOOT,
  FIGURE_HEAD,
  FIGURE_HEAD_CELL,
  FIGURE_HEAD_NAME,
  FIGURE_NAME,
  FIGURE_ROW,
  FIGURE_TABLE,
  FIGURE_TABLE_BLEED,
  FIGURE_TOGGLE,
} from '@/shared/components/figureTable';
import { ListCard } from '@/shared/components/ListCard';
import { LIST_ROW, LIST_ROWS } from '@/shared/components/listRow';
import { NoteChip } from '@/shared/components/NoteChip';
import {
  UNMEASURED_HINT,
  modelLabel,
  noRunsLine,
  notUsedLine,
  runsLine,
  showsModelLines,
  unmeasuredChipText,
} from '../aiModelsCopy';
import { quantityFill } from '../identityFill';
import {
  DELETED_PROFILE_HINT,
  UNKNOWN_PROFILE_HINT,
  type LedgerRow,
} from '../modelsLedger';
import { ModelLine } from './ModelLine';
import { ProfileMark } from './ProfileMark';
import { StepChips } from './StepChips';
import { WorkflowNameLink } from './WorkflowNameLink';

/** Cells a phone folds away (research R3): their facts move under the name. */
const WIDE_ONLY = 'hidden sm:table-cell';

const usedByText = (row: LedgerRow) =>
  row.profile
    ? `${String(row.usedBy)} ${row.usedBy === 1 ? 'workflow' : 'workflows'}`
    : '—';

const tokensText = (tokens: number | null) =>
  tokens === null ? '—' : formatTokens(tokens);

/**
 * The profiles side by side (spec 233 US2), in the Spend tab's table frame (spec 234
 * US3): one row each, sorted by runs, so runs and tokens compare down a right-aligned
 * column, a thin bar under the runs it measures and a foot that adds them up. A row
 * opens beneath itself to the workflows and steps that use the profile; a profile
 * used by six workflows stays one row tall until asked.
 */
export function ProfileLedger({
  rows,
  period,
  workspaceId,
  harnessNameOf,
}: {
  rows: LedgerRow[];
  period: PeriodBounds;
  workspaceId: string;
  /** The harness name for a profile (spec 222), found by its `ai_provider_id`. */
  harnessNameOf: (harnessId: string | null) => string | undefined;
}) {
  const profiles = rows.filter((row) => row.profile !== null).length;
  const quiet = rows.every((row) => row.runs === null);
  const totalRuns = rows.reduce((sum, row) => sum + (row.runs ?? 0), 0);
  const totalTokens = rows.some((row) => row.workTokens !== null)
    ? rows.reduce((sum, row) => sum + (row.workTokens ?? 0), 0)
    : null;
  return (
    <ListCard
      title="Profiles"
      count={profiles}
      noun={['profile', 'profiles']}
      shown={rows.length}
      total={rows.length}
      state="idle"
    >
      <div className={FIGURE_TABLE_BLEED}>
        <table className={FIGURE_TABLE}>
          <caption className="sr-only">Profiles</caption>
          <thead className={FIGURE_HEAD}>
            <tr>
              <th scope="col" className={FIGURE_HEAD_NAME}>
                Profile
              </th>
              <th
                scope="col"
                className={cn(FIGURE_HEAD_CELL, WIDE_ONLY, 'text-left')}
              >
                Model
              </th>
              {/* Wide enough for the bar under the figure, as Spend's Spend column. */}
              <th scope="col" className={cn(FIGURE_HEAD_CELL, 'w-24 sm:w-32')}>
                Runs
              </th>
              <th scope="col" className={cn(FIGURE_HEAD_CELL, WIDE_ONLY)}>
                <abbr
                  title="Input and output; cache reads not counted."
                  className="no-underline"
                >
                  Work tokens
                </abbr>
              </th>
              <th
                scope="col"
                className={cn(FIGURE_HEAD_CELL, WIDE_ONLY, 'px-4')}
              >
                Used by
              </th>
            </tr>
          </thead>
          {rows.map((row) => (
            <LedgerRowView
              key={row.key}
              row={row}
              startOpen={profiles === 1 && row.profile !== null}
              workspaceId={workspaceId}
              period={period}
              harnessName={
                row.profile
                  ? harnessNameOf(row.profile.ai_provider_id)
                  : undefined
              }
            />
          ))}
          {/* The columns, added up, under the columns — as Where it went's foot. */}
          <tfoot className={FIGURE_FOOT}>
            {quiet ? (
              <tr>
                <td
                  colSpan={5}
                  className="text-muted-foreground px-4 py-2.5 text-sm font-normal"
                >
                  {noRunsLine(period)}
                </td>
              </tr>
            ) : (
              <tr>
                <th scope="row" className="px-4 py-2.5 text-left">
                  Total
                  <span className="text-muted-foreground block text-xs font-normal sm:hidden">
                    {`${tokensText(totalTokens)} work tokens`}
                  </span>
                </th>
                <td className={WIDE_ONLY} />
                <td className={cn(FIGURE_CELL, 'py-2.5')}>
                  {String(totalRuns)}
                </td>
                <td className={cn(FIGURE_CELL, WIDE_ONLY, 'py-2.5')}>
                  {tokensText(totalTokens)}
                </td>
                <td className={WIDE_ONLY} />
              </tr>
            )}
          </tfoot>
        </table>
      </div>
    </ListCard>
  );
}

function LedgerRowView({
  row,
  startOpen,
  workspaceId,
  period,
  harnessName,
}: {
  row: LedgerRow;
  startOpen: boolean;
  workspaceId: string;
  period: PeriodBounds;
  harnessName?: string;
}) {
  // Configured but not run in the period (spec 252): it steps back, muted, with a
  // hollow dot and the period said under its name.
  const idle = row.identity.idle;
  const [open, setOpen] = useState(startOpen);
  const detailsId = useId();
  const { profile, entry } = row;
  const facts = profile ? profileFacts(profile) : null;
  const ranOn = showsModelLines(entry);
  const otherModels = profile
    ? entry.by_model.filter((line) => line.model_key !== profile.model).length
    : 0;
  // The unknown has nothing to open unless its runs name the models they used.
  const opens = profile !== null || ranOn;
  const toggle = () => {
    if (opens) setOpen((was) => !was);
  };
  const modelLine = facts ? (
    <span className="flex flex-col gap-0.5">
      <ModelLine model={facts.model} harnessName={harnessName} />
      {ranOn && otherModels > 0 ? (
        <span className="text-muted-foreground text-xs">
          {`+${String(otherModels)} ${otherModels === 1 ? 'model' : 'models'}`}
        </span>
      ) : null}
    </span>
  ) : (
    <span className="text-muted-foreground">—</span>
  );
  const usedBy = usedByText(row);
  const tokens = tokensText(row.workTokens);

  return (
    <tbody className={FIGURE_BODY}>
      <tr
        data-ledger-row={row.key}
        onClick={toggle}
        className={cn(
          FIGURE_ROW,
          opens && 'cursor-pointer',
          (!profile || idle) && 'text-muted-foreground',
        )}
      >
        <th scope="row" className={FIGURE_NAME}>
          <span className="flex min-w-0 items-start gap-3">
            {opens ? (
              <button
                type="button"
                aria-expanded={open}
                aria-controls={detailsId}
                aria-label={`Show workflows for ${row.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  toggle();
                }}
                className={cn(FIGURE_TOGGLE, 'group')}
              >
                <ChevronRightIcon
                  aria-hidden="true"
                  className="size-4 transition-transform duration-[var(--motion-duration-base)] group-aria-expanded:rotate-90 motion-reduce:transition-none"
                />
              </button>
            ) : (
              <span aria-hidden="true" className="size-7 shrink-0" />
            )}
            <span className="mt-px max-sm:hidden">
              <ProfileMark identity={row.identity} />
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                <span
                  className={cn(
                    'min-w-0 break-words',
                    profile && !idle && 'font-medium',
                  )}
                >
                  {row.name}
                </span>
                {row.deleted ? <DeletedLabel /> : null}
                {/* Run caveats are chips on the name, as on Spend (spec 225). */}
                {row.unmeasured > 0 ? (
                  <NoteChip
                    icon={CircleDashedIcon}
                    text={unmeasuredChipText(row.unmeasured)}
                    title={UNMEASURED_HINT}
                  />
                ) : null}
              </span>
              <span className="text-muted-foreground text-xs">
                {facts
                  ? idle
                    ? notUsedLine(period)
                    : facts.reasoning
                  : row.deleted
                    ? DELETED_PROFILE_HINT
                    : UNKNOWN_PROFILE_HINT}
              </span>
              {/* The Model column folds here on a phone (research R3). */}
              <span className="sm:hidden">{modelLine}</span>
              <span
                data-phone-meta=""
                className="text-muted-foreground text-xs tabular-nums sm:hidden"
              >
                {`${tokens} work tokens · ${usedBy}`}
              </span>
            </span>
          </span>
        </th>
        <td
          className={cn(FIGURE_CELL, WIDE_ONLY, 'text-left whitespace-normal')}
        >
          {modelLine}
        </td>
        <td className={FIGURE_CELL}>
          <span className="flex flex-col items-end gap-1.5">
            <span data-figure="" className={cn(profile && 'font-semibold')}>
              {row.runs === null ? '—' : String(row.runs)}
            </span>
            <span aria-hidden="true" className={FIGURE_BAR_TRACK}>
              <span
                data-runs-bar=""
                style={{ width: `${String(row.barShare * 100)}%` }}
                className={cn(FIGURE_BAR, quantityFill(row.rank))}
              />
            </span>
          </span>
        </td>
        <td data-figure="" className={cn(FIGURE_CELL, WIDE_ONLY)}>
          {tokens}
        </td>
        <td className={cn(FIGURE_CELL, WIDE_ONLY, 'px-4')}>{usedBy}</td>
      </tr>
      {opens ? (
        <tr id={detailsId} hidden={!open} className="bg-muted/20">
          <td colSpan={5} className="px-4 pt-1 pb-4 sm:ps-24">
            <RowDetails row={row} workspaceId={workspaceId} ranOn={ranOn} />
          </td>
        </tr>
      ) : null}
    </tbody>
  );
}

/** Where the profile is used, and the models its runs used when that says more. */
function RowDetails({
  row,
  workspaceId,
  ranOn,
}: {
  row: LedgerRow;
  workspaceId: string;
  ranOn: boolean;
}) {
  return (
    <div className="flex flex-col gap-3">
      {row.profile === null ? (
        <p className="text-muted-foreground text-xs">
          {row.deleted
            ? 'This profile was deleted, so no workflow uses it now.'
            : 'These runs did not record a profile.'}
        </p>
      ) : row.uses.length === 0 ? (
        <p className="text-muted-foreground text-xs">
          No workflow uses this profile now.
        </p>
      ) : (
        <ul aria-label={`Workflows using ${row.name}`} className={LIST_ROWS}>
          {row.uses.map((use) => (
            <li key={use.workflow_id} className={LIST_ROW}>
              <WorkflowNameLink
                workspaceId={workspaceId}
                workflowKey={use.workflow_key}
                workflowName={use.workflow_name}
                draft={use.draft}
              />
              <StepChips
                steps={use.steps}
                workflowKey={use.workflow_key}
                workspaceId={workspaceId}
                slot={row.identity.slot}
                idle={row.identity.idle}
                profileName={row.name}
                profileKey={row.key}
              />
            </li>
          ))}
        </ul>
      )}
      {ranOn ? (
        <div className="flex flex-col gap-1.5">
          <EyebrowLabel>Ran on</EyebrowLabel>
          <ul className="flex flex-wrap gap-x-4 gap-y-1">
            {row.entry.by_model.map((line) => (
              <li
                key={line.model_key ?? ''}
                className="flex items-center gap-2 text-sm"
              >
                <span className="font-medium">
                  {modelLabel(line.model_key)}
                </span>
                <span className="text-muted-foreground text-xs">
                  {runsLine(line.runs)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
